import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { Copy, ExternalLink, RefreshCw } from 'lucide-react';

import {
  ActionButton,
  EmptyStateIcon,
  EmptyStateText,
  EmptyStateWrapper,
  HeaderActions,
  HeaderRow,
  HeaderTitle,
  ResourceActions,
  ResourceInfo,
  ResourceItemRow,
  ResourceList,
  ResourceMeta,
  ResourceName,
  SummaryText,
} from './ResourcesTabContent.styles';

import { humanFileSize, ResourceFile } from './ResourcesTabContent.utils';
import { useResourcesTabData } from './ResourcesTabContent.hooks';

export type { ResourceFile } from './ResourcesTabContent.utils';

export interface ResourcesTabContentProps {
  courseId: string;
  disabled?: boolean;
  onResourcesChange?: (resources: ResourceFile[]) => void;
}

export const ResourcesTabContent: React.FC<ResourcesTabContentProps> = ({
  courseId,
  disabled = false,
  onResourcesChange,
}) => {
  const {
    resources,
    combinedLoading,
    error,
    handleRetry,
    totalSize,
    effectiveCourseId,
  } = useResourcesTabData({ courseId, onResourcesChange });

  const handleCopyUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('자료 URL이 복사되었습니다.');
    } catch (err) {
      console.error('[ResourcesTab] Failed to copy URL', err);
      toast.error('URL 복사에 실패했습니다.');
    }
  }, []);

  if (!effectiveCourseId) {
    return (
      <EmptyStateWrapper>
        <EmptyStateIcon />
        <EmptyStateText>
          강의를 먼저 생성하고 저장한 뒤 자료를 업로드할 수 있습니다.
        </EmptyStateText>
      </EmptyStateWrapper>
    );
  }

  if (combinedLoading && resources.length === 0) {
    return (
      <EmptyStateWrapper>
        <RefreshCw size={28} color="#94a3b8" />
        <EmptyStateText>자료 목록을 불러오는 중입니다...</EmptyStateText>
      </EmptyStateWrapper>
    );
  }

  return (
    <div>
      <HeaderRow>
        <div>
          <HeaderTitle>강의 자료</HeaderTitle>
          <SummaryText>
            총 {resources.length}개 · 사용량 {humanFileSize(totalSize) ?? '0 B'}
          </SummaryText>
        </div>
        <HeaderActions>
          <ActionButton
            type="button"
            onClick={handleRetry}
            disabled={combinedLoading || disabled}
            data-testid="course-resources-refresh-button"
          >
            <RefreshCw size={16} /> 새로고침
          </ActionButton>
        </HeaderActions>
      </HeaderRow>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#b91c1c',
          borderRadius: '0.75rem',
          padding: '0.9rem 1rem',
          marginBottom: '1rem',
        }}>
          {error}
        </div>
      )}

      {resources.length === 0 ? (
        <EmptyStateWrapper>
          <EmptyStateIcon />
          <EmptyStateText>
            업로드된 자료가 없습니다. 강의 내용에서 파일을 업로드하면 여기에서 확인할 수 있습니다.
          </EmptyStateText>
        </EmptyStateWrapper>
      ) : (
        <ResourceList>
          {resources.map((resource) => (
            <ResourceItemRow key={resource.id}>
              <ResourceInfo>
                <ResourceName
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="course-resource-name-link"
                >
                  {resource.name}
                </ResourceName>
                <ResourceMeta>
                  {(resource.sectionTitle || resource.lectureTitle) && (
                    <>
                      {resource.sectionTitle && <strong>{resource.sectionTitle}</strong>}
                      {resource.sectionTitle && resource.lectureTitle && ' · '}
                      {resource.lectureTitle}
                      {' · '}
                    </>
                  )}
                  {resource.mimeType ?? '파일'}
                  {resource.size ? ` · ${humanFileSize(resource.size)}` : ''}
                  {resource.uploadedAt ? ` · ${new Date(resource.uploadedAt).toLocaleString()}` : ''}
                </ResourceMeta>
              </ResourceInfo>

              <ResourceActions>
                <ActionButton
                  type="button"
                  onClick={() => handleCopyUrl(resource.url)}
                  disabled={disabled}
                  data-testid="course-resource-copy-button"
                >
                  <Copy size={16} /> 링크 복사
                </ActionButton>
                <ActionButton
                  as="a"
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="course-resource-open-link"
                >
                  <ExternalLink size={16} /> 열기
                </ActionButton>
              </ResourceActions>
            </ResourceItemRow>
          ))}
        </ResourceList>
      )}
    </div>
  );
};

export default ResourcesTabContent;
